import type {
  CompletedAttachment,
  MessageContentPart,
} from "../../composer/types";
import {
  buildAdditionalKwargs,
  formatAttachmentPart,
} from "../../composer/formatContent";
import { parseRecommendationFields } from "./parseCollects";
import type {
  Recommendation,
  CollectedValue,
  CollectedValues,
} from "./types";

/**
 * Tag namespace for attachments owned by a recommendation panel field.
 * `attachmentTag("website/gallery", "images")` → "rec:website/gallery:images".
 */
export function attachmentTag(recommendationId: string, field: string): string {
  return `rec:${recommendationId}:${field}`;
}

export function attachmentTagPrefix(recommendationId: string): string {
  return `rec:${recommendationId}:`;
}

export interface RecommendationSubmissionInput {
  recommendation: Recommendation;
  /** Text-field values + bespoke panel values (image fields derive from attachments). */
  collected: CollectedValues;
  /** The composer's freeform note (its text at submit time). */
  note: string;
  /**
   * The composer's COMPLETED attachments relevant to this submission:
   * untagged ones (the freeform note's) plus those tagged under this
   * recommendation's prefix. Tagged attachments group into their field;
   * untagged ride at the end like a normal chat message.
   */
  attachments: CompletedAttachment[];
}

export interface RecommendationSubmissionMessage {
  role: "human";
  content: string | MessageContentPart[];
  additional_kwargs: Record<string, unknown>;
}

/**
 * Assemble a recommendation panel submission into ONE human message — the
 * anchor the transcript renders and the backend resolves.
 *
 * Two outputs, both derived from the same filled inputs
 * (plans/recommendations.md → "Collected input: every field is content parts"):
 *
 *   1. `content` — what the agent SEES. A MessageContentPart[] interleaving
 *      each field's label with its value (text) or images (image_url blocks,
 *      so the agent has vision over them), then the freeform note and its
 *      attachments. Collapses to a plain string when no images are present.
 *
 *   2. `additional_kwargs.metadata` — the structured sidecar:
 *      `{ recommendation_id, component, collected }` for the backend's
 *      resolveRecommendation + tools + the transcript's submission view,
 *      plus `attachments[url] = { upload_id, … }` so the agent can place
 *      an image as a real website asset.
 */
export function buildRecommendationSubmission(
  input: RecommendationSubmissionInput,
): RecommendationSubmissionMessage {
  const { recommendation, note } = input;
  const fields = parseRecommendationFields(recommendation.collects);
  const prefix = attachmentTagPrefix(recommendation.recommendationId);

  const parts: MessageContentPart[] = [{ type: "text", text: recommendation.text }];
  const collectedOut: CollectedValues = {};
  const consumedTags = new Set<string>();
  const consumedKeys = new Set<string>();

  // 1. Declared fields, in collects order — the registry-authored structure.
  for (const field of fields) {
    if (field.accepts === "image") {
      const tag = attachmentTag(recommendation.recommendationId, field.name);
      consumedTags.add(tag);
      const images = input.attachments.filter((a) => a.tag === tag);
      if (images.length === 0) continue;
      parts.push({ type: "text", text: `${field.prompt}:` });
      for (const image of images) parts.push(formatAttachmentPart(image));
      const urls = images.map((a) => a.url);
      collectedOut[field.name] = field.multiple ? urls : urls[0]!;
    } else if (field.fields) {
      // Group: one labeled line per record, subfields named by their prompts.
      const value = input.collected[field.name];
      consumedKeys.add(field.name);
      if (!Array.isArray(value)) continue;
      const itemLabel = field.itemLabel ?? "item";
      const cleaned: Array<Record<string, string>> = [];
      for (const raw of value) {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw)) continue;
        const record: Record<string, string> = {};
        for (const sub of field.fields) {
          const text = (raw as Record<string, unknown>)[sub.name];
          if (typeof text === "string" && text.trim() !== "") record[sub.name] = text.trim();
        }
        if (Object.keys(record).length > 0) cleaned.push(record);
      }
      if (cleaned.length === 0) continue;
      parts.push({ type: "text", text: `${field.prompt}:` });
      cleaned.forEach((record, i) => {
        const line = field
          .fields!.filter((sub) => record[sub.name] !== undefined)
          .map((sub) => `${sub.prompt}: ${record[sub.name]}`)
          .join(" — ");
        parts.push({ type: "text", text: `${itemLabel} ${i + 1} — ${line}` });
      });
      collectedOut[field.name] = cleaned;
    } else {
      const value = input.collected[field.name];
      consumedKeys.add(field.name);
      if (typeof value !== "string" || value.trim() === "") continue;
      parts.push({ type: "text", text: `${field.prompt}: ${value.trim()}` });
      collectedOut[field.name] = value.trim();
    }
  }

  // 2. Bespoke values the panel set outside any declared field
  //    (a ColorPicker's palette, a Testimonials panel's records).
  for (const [key, value] of Object.entries(input.collected)) {
    if (consumedKeys.has(key)) continue;
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      if (value.trim() === "") continue;
      parts.push({ type: "text", text: `${labelize(key)}: ${value.trim()}` });
      collectedOut[key] = value.trim();
    } else if (isStringArray(value)) {
      if (value.length === 0) continue;
      parts.push({ type: "text", text: `${labelize(key)}: ${value.join(", ")}` });
      collectedOut[key] = value;
    } else {
      // Array of records — one line per record, fields joined readably.
      const records = value.filter((r) => Object.values(r).some((v) => v?.trim?.()));
      if (records.length === 0) continue;
      records.forEach((record, i) => {
        const line = Object.entries(record)
          .filter(([, v]) => typeof v === "string" && v.trim() !== "")
          .map(([k, v]) => `${labelize(k)}: ${v.trim()}`)
          .join(" — ");
        parts.push({ type: "text", text: `${labelize(key)} ${i + 1} — ${line}` });
      });
      collectedOut[key] = records;
    }
  }

  // 3. Remaining tagged attachment groups under this recommendation —
  //    bespoke panels tag freely (e.g. a testimonial's headshot_2); group
  //    by tag in insertion order, labeled by tagLabel (or the tag suffix).
  const remainingTagged = input.attachments.filter(
    (a) => a.tag && a.tag.startsWith(prefix) && !consumedTags.has(a.tag),
  );
  const groups = new Map<string, CompletedAttachment[]>();
  for (const a of remainingTagged) {
    const list = groups.get(a.tag!) ?? [];
    list.push(a);
    groups.set(a.tag!, list);
  }
  for (const [tag, group] of groups) {
    const fieldKey = tag.slice(prefix.length);
    const label = group[0]!.tagLabel ?? labelize(fieldKey);
    parts.push({ type: "text", text: `${label}:` });
    for (const image of group) parts.push(formatAttachmentPart(image));
    const urls = group.map((a) => a.url);
    collectedOut[fieldKey] = urls.length === 1 ? urls[0]! : urls;
  }

  // 4. The freeform note + its (untagged) attachments — "still your chat".
  if (note.trim() !== "") {
    parts.push({ type: "text", text: note.trim() });
  }
  for (const attachment of input.attachments.filter((a) => !a.tag)) {
    parts.push(formatAttachmentPart(attachment));
  }

  const content = normalizeParts(parts);

  // Sidecar: per-attachment metadata (upload_id, filenames) + the click
  // contract. `component` rides along so the transcript can re-render the
  // submission view from history without the (ephemeral) recommendations
  // channel.
  const attachmentKwargs = buildAdditionalKwargs(input.attachments) ?? {};
  const attachmentMetadata =
    (attachmentKwargs as { metadata?: Record<string, unknown> }).metadata ?? {};
  const additional_kwargs: Record<string, unknown> = {
    ...attachmentKwargs,
    metadata: {
      ...(note.trim() ? { note: note.trim() } : {}),
      ...attachmentMetadata,
      recommendation_id: recommendation.recommendationId,
      ...(recommendation.component ? { component: recommendation.component } : {}),
      ...(Object.keys(collectedOut).length > 0 ? { collected: collectedOut } : {}),
    },
  };

  return { role: "human", content, additional_kwargs };
}

/** Merge adjacent text parts; collapse to a plain string when text-only. */
function normalizeParts(
  parts: MessageContentPart[],
): string | MessageContentPart[] {
  const merged: MessageContentPart[] = [];
  for (const part of parts) {
    const prev = merged[merged.length - 1];
    if (part.type === "text" && prev?.type === "text") {
      prev.text = `${prev.text}\n${part.text}`;
    } else {
      merged.push(part.type === "text" ? { ...part } : part);
    }
  }
  if (merged.length === 1 && merged[0]!.type === "text") {
    return (merged[0] as { type: "text"; text: string }).text;
  }
  return merged;
}

function isStringArray(value: CollectedValue): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

function labelize(key: string): string {
  return key.replace(/_/g, " ");
}
